import React from 'react';
import PropTypes from 'prop-types';
import {
  Box, Button, IconButton, Paper, TextField, MenuItem, InputAdornment, Typography, Tooltip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import * as C from '../../theme/colors.constants';

/**
 * Primitivos de UI do protótipo.
 *
 * Cada um espelha um componente que já existe no abe-web-react
 * (`src/components/UI/...`), mantendo a mesma intenção de API para que a
 * substituição futura seja direta. As diferenças estão comentadas.
 */

/** Equivale a `UI/Paper/PaperUI` + `UI/CardWrapper`. */
export const CardWrapper = styled(Paper)(({ theme }) => ({
  padding: 'clamp(16px, 1.15vw, 30px)',
  borderRadius: C.radiusCard,
  boxShadow: C.shadowCard,
  backgroundColor: C.white,
  width: '100%',
  // minWidth: 0 permite o card encolher dentro de um flex/grid; sem isso o
  // conteúdo (tabelas largas) define o piso e o layout gera scroll lateral.
  minWidth: 0,
  overflow: 'hidden',
  [theme.breakpoints.down('lg')]: { padding: 16 },
}));

/** Equivale a `UI/Button/ButtonUI` (variante `primary`, pill navy preenchido). */
export function ButtonUI({ children, variant = 'contained', ...rest }) {
  return (
    <Button variant={variant} color="primary" {...rest}>
      {children}
    </Button>
  );
}
ButtonUI.propTypes = { children: PropTypes.node, variant: PropTypes.string };
ButtonUI.defaultProps = { children: null, variant: 'contained' };

/** Equivale a `ButtonOutlined/ButtonOurlined` — pill de contorno navy. */
export function ButtonOutlined({ children, ...rest }) {
  return (
    <Button variant="outlined" color="primary" {...rest}>
      {children}
    </Button>
  );
}
ButtonOutlined.propTypes = { children: PropTypes.node };
ButtonOutlined.defaultProps = { children: null };

/** Label acima do campo, como nos prints (não é o label flutuante do MUI). */
export function FieldLabel({ children, sx }) {
  return (
    <Typography
      component="label"
      sx={{
        display: 'block',
        fontSize: 'clamp(11px, 0.62vw, 14px)',
        fontWeight: 700,
        color: C.black,
        mb: 0.5,
        ...sx,
      }}
    >
      {children}
    </Typography>
  );
}
FieldLabel.propTypes = { children: PropTypes.node, sx: PropTypes.object };
FieldLabel.defaultProps = { children: null, sx: {} };

/**
 * Campo de texto/select/data com label externo.
 * Cobre `UI/TextField/TextFieldUI`, `UI/Select/SelectUI` e `UI/DateTextInput`
 * numa única assinatura, porque no protótipo não há validação real.
 */
export function Field({
  label, tipo = 'texto', options = [], value = '', onChange, placeholder, fullWidth = true, sx, ...rest
}) {
  const isSelect = tipo === 'select';
  const isDate = tipo === 'data';
  return (
    <Box sx={{ width: fullWidth ? '100%' : undefined, ...sx }}>
      {label ? <FieldLabel>{label}</FieldLabel> : null}
      <TextField
        select={isSelect}
        type={isDate ? 'date' : 'text'}
        size="small"
        fullWidth={fullWidth}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        SelectProps={{ displayEmpty: true }}
        inputProps={isDate ? { 'aria-label': label } : undefined}
        {...rest}
      >
        {isSelect
          ? [
            <MenuItem key="__blank" value="">
              <em style={{ color: C.greyish }}>Selecione</em>
            </MenuItem>,
            ...options.map(o => (
              <MenuItem key={o} value={o}>
                {o}
              </MenuItem>
            )),
          ]
          : null}
      </TextField>
    </Box>
  );
}
Field.propTypes = {
  label: PropTypes.string,
  tipo: PropTypes.oneOf(['texto', 'select', 'data']),
  options: PropTypes.array,
  value: PropTypes.any,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  fullWidth: PropTypes.bool,
  sx: PropTypes.object,
};
Field.defaultProps = {
  label: null,
  tipo: 'texto',
  options: [],
  value: '',
  onChange: () => null,
  placeholder: '',
  fullWidth: true,
  sx: {},
};

/** Par de datas "de / e / até", como em "Abertos entre:". */
export function PeriodoField({
  label, valor = {}, onChange, separador = 'e',
}) {
  const set = k => e => onChange && onChange({ ...valor, [k]: e.target.value });
  return (
    <Box sx={{ width: '100%' }}>
      {label ? <FieldLabel>{label}</FieldLabel> : null}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <TextField type="date" size="small" fullWidth value={valor.inicio || ''} onChange={set('inicio')} />
        <Typography variant="body2" sx={{ color: C.warmGrey, flexShrink: 0 }}>
          {separador}
        </Typography>
        <TextField type="date" size="small" fullWidth value={valor.fim || ''} onChange={set('fim')} />
      </Box>
    </Box>
  );
}
PeriodoField.propTypes = {
  label: PropTypes.string,
  valor: PropTypes.object,
  onChange: PropTypes.func,
  separador: PropTypes.string,
};
PeriodoField.defaultProps = {
  label: null,
  valor: {},
  onChange: () => null,
  separador: 'e',
};

/** Equivale a `SearchButton/SeachButton`, agora em navy no lugar do rosa. */
export function SearchIconButton({ sx, ...rest }) {
  return (
    <IconButton
      sx={{
        color: C.navy,
        '&:hover': { backgroundColor: 'rgba(13,43,82,0.08)' },
        ...sx,
      }}
      {...rest}
    >
      <SearchIcon />
    </IconButton>
  );
}
SearchIconButton.propTypes = { sx: PropTypes.object };
SearchIconButton.defaultProps = { sx: {} };

/** Campo de busca com lupa embutida (usado na home e no topo da grid). */
export function SearchField({
  placeholder, value, onChange, onSubmit, sx,
}) {
  return (
    <TextField
      size="small"
      fullWidth
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      onKeyDown={e => e.key === 'Enter' && onSubmit && onSubmit()}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <SearchIconButton size="small" onClick={onSubmit} />
          </InputAdornment>
        ),
      }}
      sx={sx}
    />
  );
}
SearchField.propTypes = {
  placeholder: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
  onSubmit: PropTypes.func,
  sx: PropTypes.object,
};
SearchField.defaultProps = {
  placeholder: '',
  value: '',
  onChange: () => null,
  onSubmit: () => null,
  sx: {},
};

/** Equivale a `UI/Icon/WhiteCircleIcon` — o "chip" branco dos ícones de alerta. */
export const WhiteCircleIcon = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 20,
  height: 20,
  borderRadius: '50%',
  backgroundColor: C.white,
  boxShadow: C.shadowChip,
  flexShrink: 0,
  lineHeight: 0,
});

/** Título de seção dentro de um card. Equivale a `Title/Title`. */
export function SectionTitle({
  children, hint, right, sx,
}) {
  const node = (
    <Typography variant="h3" sx={{ color: C.navy, ...sx }}>
      {children}
    </Typography>
  );
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 1.5,
    }}
    >
      {hint ? <Tooltip title={hint} arrow>{node}</Tooltip> : node}
      {right}
    </Box>
  );
}
SectionTitle.propTypes = {
  children: PropTypes.node,
  hint: PropTypes.string,
  right: PropTypes.node,
  sx: PropTypes.object,
};
SectionTitle.defaultProps = {
  children: null,
  hint: null,
  right: null,
  sx: {},
};

/** Formatação monetária pt-BR usada em toda a grid. */
export const formatBRL = v => (v ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
